import { z } from 'zod';
import { FleetProjectNameSchema } from './fleet-status.schemas';

/**
 * `POST /v1/fleet/jobs/next` — the backend's pick and claim in one step (fleet-agent ADR 0011, rollout step 3). A
 * lane sends its queues, the backend filters its tracker snapshot, claims the first ticket no live job holds, and
 * moves it to the lane's running state. The backend keeps no lane config — every project value rides in the request.
 *
 * Mirrors fleet-agent's `src/presenter/contract/fleet-next-job.schemas.ts`; keep the shapes identical. The pick rules
 * are written out on fleet-agent's `src/domain/fleet/next-job.ts` and on the backend's `fleet-pick.ts`.
 */

const NameSchema = z.string().min(1).max(128);
const LabelSchema = z.string().min(1).max(128);

export const FLEET_TICKET_WRITE_RESULTS = ['moved', 'already', 'left', 'parked', 'failed'] as const;
export type FleetTicketWriteResult = (typeof FLEET_TICKET_WRITE_RESULTS)[number];

/**
 * A batch queue (the deployer lane): it takes one ticket, the first in the normal order, only when a batch is due.
 *
 * - `holdLabel` on any issue in the queue's states: the queue takes nothing (the batch is on hold).
 * - Any issue in the queue's states held by a live job under any lane: the queue takes nothing (one batch at a time).
 * - Otherwise it takes iff the issues it takes today number at least `minCount`, or one of them has waited
 *   `maxAgeMinutes` or more in its state (`stateEnteredAt`, else `createdAt`). A pinned run (`ticket`) skips this last
 *   test only. The rule is `batchDue`/`batchTakes` on the backend's `fleet-pick.ts`.
 */
export const FleetPickBatchSchema = z.object({
  minCount: z.number().int().min(1).max(100),
  maxAgeMinutes: z.number().int().min(1).max(43200),
  holdLabel: LabelSchema.nullable(),
});
export type FleetPickBatch = z.infer<typeof FleetPickBatchSchema>;

/** A lane's queue, as the tracker spells its states and labels. */
export const FleetPickQueueSchema = z.object({
  tracker: z.literal('linear'),
  team: NameSchema,
  states: z.array(NameSchema).min(1).max(20),
  with: z.array(LabelSchema).max(20),
  without: z.array(LabelSchema).max(20),
  /** Absent: an ordinary queue. */
  batch: FleetPickBatchSchema.optional(),
});
export type FleetPickQueue = z.infer<typeof FleetPickQueueSchema>;

/** Where the backend moves a claimed ticket; `null`: it stays where it was picked. */
export const FleetRunningStateSchema = z.object({ tracker: z.literal('linear'), state: NameSchema }).nullable();
export type FleetRunningState = z.infer<typeof FleetRunningStateSchema>;

export const ClaimNextFleetJobBodySchema = z.object({
  project: FleetProjectNameSchema,
  /** The lane's name and key: the job's key is `{ticket}-{lane}`, as today, so its history and run cap carry on. */
  lane: z.string().min(1).max(64),
  worker: z.string().min(1).max(128),
  pickFrom: z.array(FleetPickQueueSchema).min(1).max(10),
  running: FleetRunningStateSchema,
  /**
   * Claim this ticket and no other (ADR 0011 step 4b): a pinned run, or a job resumed on its answer. It must sit in
   * one of the queues' states; `with`, `without` and the order do not apply. The exclusivity and the run cap do: a
   * held ticket is refused `already-claimed`, a capped one `run-limit`. `job: null` when it is in none of the states.
   */
  ticket: z.string().min(1).max(64).optional(),
});
export type ClaimNextFleetJobBody = z.infer<typeof ClaimNextFleetJobBodySchema>;

/** The answer a parked ticket was resumed with. */
export const FleetResumeAnswerSchema = z.object({
  question: z.string(),
  answer: z.string(),
  answeredBy: z.string(),
  answeredAt: z.iso.datetime(),
});
export type FleetResumeAnswer = z.infer<typeof FleetResumeAnswerSchema>;

export const ClaimedFleetJobSchema = z.object({
  jobId: z.uuid(),
  claimEpoch: z.number().int().positive(),
  claimedAt: z.iso.datetime(),
  key: z.string(),
  ticket: z.string(),
  title: z.string(),
  url: z.url(),
  /** The state the ticket was picked from — where a release puts it back. */
  pickedFrom: z.string(),
  /** The state it is in now: `running`'s, or `pickedFrom` when the lane names none or the move failed. */
  state: z.string(),
  answer: FleetResumeAnswerSchema.nullable(),
});
export type ClaimedFleetJob = z.infer<typeof ClaimedFleetJobSchema>;

/** `job: null`: nothing in the queues is takeable. A worker that still holds a job is refused, as `/claim` does. */
export const ClaimNextFleetJobResponseSchema = z.object({ job: ClaimedFleetJobSchema.nullable() });
export type ClaimNextFleetJobResponse = z.infer<typeof ClaimNextFleetJobResponseSchema>;

/** Sent with a job's end: the lane's `move-to` target, moved only from a state the lane owns. */
export const FleetTicketMoveSchema = z.object({ to: NameSchema, from: z.array(NameSchema).min(1).max(20) });
export type FleetTicketMove = z.infer<typeof FleetTicketMoveSchema>;

/**
 * Sent with a `parked` end: the label goes on, the ticket goes back to where the job picked it. Every other end that
 * is not `finished` — `no-run`, `abandoned`, a `parked` end without one — puts it back too, when it still sits in the
 * job's running state (ADR 0011 step 4b).
 */
export const FleetTicketParkSchema = z.object({ label: LabelSchema });
export type FleetTicketPark = z.infer<typeof FleetTicketParkSchema>;

/** What became of the ticket: where it is now, and a line a log carries when it was not moved. */
export const FleetTicketWriteSchema = z.object({
  result: z.enum(FLEET_TICKET_WRITE_RESULTS),
  state: z.string().nullable(),
  detail: z.string().nullable(),
});
export type FleetTicketWrite = z.infer<typeof FleetTicketWriteSchema>;
